import { Activity, CheckCircle2, Database, Server, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';

import { useHealth } from './api';
import { formatTimestamp } from './shared';

const API_URL = import.meta.env.VITE_API_URL ?? 'same-origin';
const BUILD_SHA = import.meta.env.VITE_BUILD_SHA ?? 'dev';
const ENV = import.meta.env.VITE_ENV ?? 'local';

export function HealthTab() {
  const { data, isLoading, error } = useHealth();
  const apiOk = !!data && !error && data.api === 'online';
  const dbOk = apiOk && data.db === 'connected';
  const apiDetail = API_URL === 'same-origin' ? 'Local proxy /v1 -> backend' : API_URL;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <HealthTile
          icon={Server}
          label="Backend API"
          value={apiOk ? 'Online' : 'Unavailable'}
          status={isLoading ? 'pending' : apiOk ? 'ok' : 'bad'}
          detail={apiDetail}
        />
        <HealthTile
          icon={Database}
          label="Database"
          value={dbOk ? 'Connected' : 'Check required'}
          status={isLoading ? 'pending' : dbOk ? 'ok' : 'bad'}
          detail={data?.error ?? 'Reported by /v1/admin/health'}
        />
        <HealthTile
          icon={Activity}
          label="Admin build"
          value={String(ENV)}
          status="neutral"
          detail={`Build ${BUILD_SHA}`}
        />
      </div>

      <div className="rounded-md border bg-card px-4 py-3 text-sm">
        <div className="font-medium">Last health check</div>
        <div className="mt-1 text-muted-foreground">
          {isLoading
            ? 'Checking...'
            : data?.timestamp
              ? formatTimestamp(data.timestamp)
              : error instanceof Error
                ? error.message
                : 'No health response yet.'}
        </div>
      </div>
    </div>
  );
}

type TileStatus = 'ok' | 'bad' | 'pending' | 'neutral';

interface HealthTileProps {
  icon: LucideIcon;
  label: string;
  value: string;
  status: TileStatus;
  detail: string;
}

function HealthTile({ icon: Icon, label, value, status, detail }: HealthTileProps) {
  const badge =
    status === 'ok' ? (
      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        OK
      </Badge>
    ) : status === 'bad' ? (
      <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
        <XCircle className="h-3.5 w-3.5" />
        Attention
      </Badge>
    ) : status === 'pending' ? (
      <Badge variant="outline">Checking</Badge>
    ) : (
      <Badge variant="outline">Info</Badge>
    );

  return (
    <div className="rounded-md border bg-card px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="mt-1 text-lg font-medium text-foreground">{value}</div>
        </div>
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="truncate text-xs text-muted-foreground" title={detail}>
          {detail}
        </div>
        {badge}
      </div>
    </div>
  );
}
